import { useCallback, useEffect, useMemo, useState } from 'react';

import storage from '@/providers/LocalStorageState/storage';

const STORAGE_PREFIX = 'conversation-cache';
const MAX_CONVERSATIONS = 50; // Limit cached conversations
export const MAX_MESSAGES_PER_CONVERSATION = 10; // Limit messages cached per conversation (matches largest page size)
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE_KB = 500; // Maximum total cache size in KB (~300KB for 10 conversations)

type CachedMessages<T> = {
  data: T;
  timestamp: number;
};

type ConversationCacheMap = {
  [conversationKey: string]: CachedMessages<unknown>;
};

const estimateSizeInKb = (data: unknown): number => {
  try {
    const json = JSON.stringify(data);
    return new Blob([json]).size / 1024;
  } catch {
    return 0;
  }
};

const getCacheSizeInKb = (cache: ConversationCacheMap): number =>
  Object.values(cache).reduce((total, entry) => total + estimateSizeInKb(entry.data), 0);

const conversationCache = {
  // Cleanup expired entries
  cleanup: (creatorId: string): void => {
    try {
      const existing = storage.get(STORAGE_PREFIX, creatorId);
      if (!existing) return;

      const cache: ConversationCacheMap = JSON.parse(existing);
      const now = Date.now();
      const cleaned: ConversationCacheMap = {};

      Object.entries(cache).forEach(([key, value]) => {
        if (now - value.timestamp <= MAX_AGE_MS) {
          cleaned[key] = value;
        }
      });

      storage.set(STORAGE_PREFIX, creatorId, JSON.stringify(cleaned));
    } catch (error) {
      console.error('Failed to cleanup conversation cache:', error);
    }
  },

  clear: (creatorId: string): void => {
    try {
      storage.remove(STORAGE_PREFIX, creatorId);
    } catch (error) {
      console.error('Failed to clear conversation cache:', error);
    }
  },

  get: <T>(creatorId: string, conversationKey: string): T | null => {
    try {
      const cacheData = storage.get(STORAGE_PREFIX, creatorId);
      if (!cacheData) return null;

      const cache: ConversationCacheMap = JSON.parse(cacheData);
      const entry = cache[conversationKey];

      if (!entry) return null;

      // Check if expired
      if (Date.now() - entry.timestamp > MAX_AGE_MS) {
        conversationCache.remove(creatorId, conversationKey);
        return null;
      }

      return entry.data as T;
    } catch (error) {
      console.error('Failed to read conversation cache:', error);
      return null;
    }
  },

  remove: (creatorId: string, conversationKey: string): void => {
    try {
      const existing = storage.get(STORAGE_PREFIX, creatorId);
      if (!existing) return;

      const cache: ConversationCacheMap = JSON.parse(existing);
      delete cache[conversationKey];
      storage.set(STORAGE_PREFIX, creatorId, JSON.stringify(cache));
    } catch (error) {
      console.error('Failed to remove from conversation cache:', error);
    }
  },

  set: <T>(creatorId: string, conversationKey: string, data: T): void => {
    try {
      let cache: ConversationCacheMap = {};

      const existing = storage.get(STORAGE_PREFIX, creatorId);
      if (existing) {
        cache = JSON.parse(existing);
      }

      // Check size of new data before adding
      const newDataSizeKB = estimateSizeInKb(data);
      if (newDataSizeKB > MAX_CACHE_SIZE_KB / 2) {
        // Single conversation is more than half the max size - skip caching
        console.warn('Conversation too large to cache', { conversationKey, sizeKB: newDataSizeKB });
        return;
      }

      // Clean up expired entries first
      const now = Date.now();
      const cleaned: ConversationCacheMap = {};
      Object.entries(cache).forEach(([key, value]) => {
        if (now - value.timestamp <= MAX_AGE_MS) {
          cleaned[key] = value;
        }
      });
      cache = cleaned;

      // Add new entry
      cache[conversationKey] = {
        data,
        timestamp: Date.now(),
      };

      const entries = Object.entries(cache);
      if (entries.length > MAX_CONVERSATIONS) {
        const sorted = entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
        cache = Object.fromEntries(sorted.slice(0, MAX_CONVERSATIONS));
      }

      let currentSizeKB = getCacheSizeInKb(cache);
      while (currentSizeKB > MAX_CACHE_SIZE_KB && Object.keys(cache).length > 1) {
        const sorted = Object.entries(cache).sort((a, b) => a[1].timestamp - b[1].timestamp);
        const oldestKey = sorted[0][0];
        delete cache[oldestKey];
        currentSizeKB = getCacheSizeInKb(cache);
      }

      // If still too large after removing all but one, skip caching
      if (currentSizeKB > MAX_CACHE_SIZE_KB) {
        console.warn('Cache size limit exceeded, skipping cache', { sizeKB: currentSizeKB });
        return;
      }

      storage.set(STORAGE_PREFIX, creatorId, JSON.stringify(cache));
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing all conversation caches');
        try {
          storage.clear(STORAGE_PREFIX);
        } catch (cleanupError) {
          console.error('Failed to cleanup conversation caches:', cleanupError);
        }
      } else {
        console.error('Failed to write conversation cache:', error);
      }
    }
  },
};

type UseConversationMessagesCacheReturn<T> = {
  cachedMessages: T | null;
  isFreshData: boolean;
  cacheMessages: (messages: T) => void;
  clearCache: () => void;
};

export const useConversationMessagesCache = <T>({
  creatorId,
  conversationKey,
}: {
  creatorId: string | null;
  conversationKey: string | null;
}): UseConversationMessagesCacheReturn<T> => {
  const [cacheVersion, setCacheVersion] = useState(0);

  const cachedMessages = useMemo<T | null>(() => {
    if (creatorId && conversationKey) {
      const cached = conversationCache.get<T>(creatorId, conversationKey);
      return cached ?? null;
    }
    return null;
    // cacheVersion is a required dependency to ensure that useMemo is re-computed when conversationKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, conversationKey, cacheVersion]);

  const [isFreshData, setIsFreshData] = useState(false);

  useEffect(() => {
    setIsFreshData(false);
  }, [conversationKey]);

  useEffect(() => {
    if (creatorId) {
      conversationCache.cleanup(creatorId);
    }
  }, [creatorId]);

  const cacheMessages = useCallback(
    (messages: T) => {
      if (creatorId && conversationKey) {
        // Limit number of messages cached to reduce size
        let messagesToCache = messages;
        if (Array.isArray(messages) && messages.length > MAX_MESSAGES_PER_CONVERSATION) {
          messagesToCache = messages.slice(0, MAX_MESSAGES_PER_CONVERSATION) as T;
        }
        conversationCache.set(creatorId, conversationKey, messagesToCache);
        setIsFreshData(true);
        setCacheVersion((v) => v + 1); // Trigger useMemo re-computation
      }
    },
    [creatorId, conversationKey],
  );

  const clearCache = useCallback(() => {
    if (creatorId && conversationKey) {
      conversationCache.remove(creatorId, conversationKey);
      setIsFreshData(false);
      setCacheVersion((v) => v + 1); // Trigger useMemo re-computation
    }
  }, [creatorId, conversationKey]);

  return {
    cacheMessages,
    cachedMessages,
    clearCache,
    isFreshData,
  };
};
